// "Explain the number" drawer: walks one takeoff line from surface defaults to $.
import { AlertCircle, Calculator, X } from "lucide-react";
import { IconButton, Pill } from "../../../../components/ui";
import { fmtCurrency, fmtDec2, fmtHrs, fmtPct, fmtQty } from "../../../../utils/format";

const PRIMER_RULES = { ALWAYS: "Always", NEW: "New construction only", NEVER: "Never" };

function Section({ n, title, children }) {
  return (
    <section className="border-b border-border px-4 py-3">
      <h4 className="mb-2 flex items-center gap-2 text-[11px] font-semibold uppercase tracking-wide text-text_muted">
        <span className="flex h-[18px] w-[18px] items-center justify-center rounded-full bg-primary text-[10px] text-white">{n}</span>
        {title}
      </h4>
      <div className="flex flex-col gap-1.5">{children}</div>
    </section>
  );
}

function Row({ label, value, hint, strong, tone }) {
  return (
    <div className="flex items-start justify-between gap-3 text-[12px]">
      <span className="text-text_secondary">{label}</span>
      <span className={`text-right tabular-nums ${strong ? "font-semibold text-text_primary" : tone === "info" ? "font-medium text-info" : "text-text_primary"}`}>
        {value}
        {hint && <span className="block text-[10.5px] font-normal text-text_muted">{hint}</span>}
      </span>
    </div>
  );
}

function Formula({ expr, result }) {
  return (
    <div className="rounded-[4px] border border-border_soft bg-surface_alt px-2.5 py-1.5 font-mono text-[11px] leading-relaxed text-text_secondary">
      {expr}
      <span className="block text-right font-sans text-[12px] font-semibold text-text_primary">= {result}</span>
    </div>
  );
}

export default function LineInspector({ line, result, defaults, rowNumber, config, idx, project, onClose }) {
  if (!line) {
    return (
      <aside className="flex h-full w-[360px] flex-shrink-0 flex-col items-center justify-center gap-2 rounded-[4px] border border-border bg-surface p-6 text-center" aria-label="Line inspector">
        <Calculator size={22} className="text-text_muted" aria-hidden="true" />
        <p className="text-[12.5px] text-text_secondary">Click a takeoff row to see how its numbers are calculated.</p>
      </aside>
    );
  }

  const s = result?.surface;
  const label = (field, v) => {
    if (field === "access") return idx.bands[v]?.label || v;
    if (typeof v === "boolean") return v ? "Yes" : "No";
    return v === "" || v == null ? "—" : String(v);
  };
  const effective = ["coats", "primer", "topcoat", "sspcPrep", "subbed", "access", "finishLevel", "condition", "tool", "finishProductId", "primerProductId"];
  const fieldNames = {
    coats: "Coats", primer: "Primer", topcoat: "Topcoat", sspcPrep: "SSPC prep", subbed: "Subbed", access: "Access",
    finishLevel: "Finish level", condition: "Condition", tool: "Field tool", finishProductId: "Finish product", primerProductId: "Primer product",
  };
  const r = result || {};
  const rate = config.globalAssumptions.laborRatePainting;

  return (
    <aside className="flex h-full w-[360px] flex-shrink-0 flex-col overflow-hidden rounded-[4px] border border-border bg-surface" aria-label="Line inspector">
      <header className="flex items-start justify-between gap-2 border-b border-border bg-primary px-4 py-3 text-white">
        <div className="min-w-0">
          <p className="text-[10.5px] font-semibold uppercase tracking-wide text-white/70">Line {rowNumber} · Inspector</p>
          <p className="truncate text-[13px] font-semibold" title={line.rawName}>{line.rawName}</p>
          <p className="truncate text-[11px] text-white/70">{line.area}</p>
        </div>
        <IconButton icon={X} label="Close inspector" size="xs" variant="ghost" className="text-white" onClick={onClose} />
      </header>

      <div className="flex-1 overflow-y-auto">
        {!r.valid && (
          <div className="m-3 flex items-start gap-2 rounded-[4px] border border-danger_border bg-danger_bg px-3 py-2 text-[12px] text-danger" role="alert">
            <AlertCircle size={14} className="mt-0.5 flex-shrink-0" aria-hidden="true" />
            <span>{Object.values(r.errors || {}).join(" · ")} — this line is excluded from totals until fixed.</span>
          </div>
        )}

        {s && (
          <Section n={1} title="Surface defaults">
            <Row label="Surface" value={s.name} hint={`${s.group} · ${s.side === "EXT" ? "Exterior" : "Interior"}`} strong />
            <Row label="Unit / SF per unit" value={`${s.unit} · ${s.sfPerUnit}`} />
            <Row label="Coverage factor" value={fmtDec2(s.factor)} />
            <Row label="Default tool / cut-in" value={`${s.defaultTool} · ${fmtPct(s.cutPct)}`} />
            <Row label="Primer rule" value={PRIMER_RULES[s.primerRule]} />
            <Row label="Productivity (SF/hr)" value={Object.entries(s.productivity).map(([t, v]) => `${t.replace("Airless Spray", "Airless")} ${v}`).join(" · ")} />
          </Section>
        )}

        <Section n={2} title="Effective values">
          {effective.map((f) => {
            const overridden = defaults && defaults[f] !== undefined && line[f] !== defaults[f];
            return (
              <Row
                key={f}
                label={fieldNames[f]}
                value={label(f, line[f])}
                hint={overridden ? `default: ${label(f, defaults[f])}` : undefined}
                tone={overridden ? "info" : undefined}
              />
            );
          })}
          <Row label="Difficulty factor (project)" value={`× ${fmtDec2(project.difficultyFactor)}`} />
          <Row label="Brand mode (project)" value={project.brandMode} />
        </Section>

        {r.valid && line.subbed && (
          <Section n={3} title="Subcontract">
            <Formula expr={`${fmtQty(r.sfQty)} SF × ${fmtCurrency(r.subRate)}/SF`} result={fmtCurrency(r.subCost)} />
            <p className="text-[11px] text-text_muted">Subbed lines carry no self-perform hours or material; the {fmtPct(config.globalAssumptions.subMarkupPct)} sub markup is added on the Bid Form.</p>
          </Section>
        )}

        {r.valid && !line.subbed && (
          <>
            <Section n={3} title="Coverage">
              <Row label="SF quantity" value={`${fmtQty(r.sfQty)} SF`} hint={line.unit === "SF" ? "native SF" : `${fmtQty(line.nativeQty)} ${line.unit} × ${s.sfPerUnit}`} />
              {r.finishProduct ? (
                <Formula expr={`${r.finishProduct.id} ${r.finishProduct.coverage} SF/gal × factor ${fmtDec2(s.factor)}`} result={`${fmtQty(r.coverage)} SF/gal`} />
              ) : (
                <Row label="Finish product" value="—" />
              )}
              <Row label={`Tool waste (${line.tool})`} value={fmtPct(r.toolWaste)} />
            </Section>

            <Section n={4} title="Gallons">
              <Formula expr={`${fmtQty(r.sfQty)} SF × ${r.finishCoats} coats ÷ ${fmtQty(r.coverage)} × (1 + ${fmtPct(r.toolWaste)})`} result={`${fmtQty(r.finishGal)} gal finish`} />
              {r.primerProduct ? (
                <Formula expr={`${fmtQty(r.sfQty)} SF ÷ ${r.primerCoverage} (${r.primerProduct.id}) × (1 + ${fmtPct(r.toolWaste)})`} result={`${fmtQty(r.primerGal)} gal primer`} />
              ) : (
                <Row label="Primer" value="Not applied" />
              )}
            </Section>

            <Section n={5} title="Hours">
              <Row label="Passes (coats + primer)" value={`${r.finishCoats} + ${r.primerPasses} = ${r.passes}`} />
              <Row label={`Access (${idx.bands[line.access]?.short})`} value={`× ${fmtDec2(r.accessMult)}`} />
              <Formula expr={`${fmtQty(r.sfQty)} × (1 − ${fmtPct(r.cutPct)}) × ${r.passes} ÷ ${r.prodTool} SF/hr (${line.tool}) × ${fmtDec2(r.accessMult)}`} result={`${fmtHrs(r.fieldHrs)} field hrs`} />
              <Formula expr={`${fmtQty(r.sfQty)} × ${fmtPct(r.cutPct)} × ${r.passes} ÷ ${r.prodBrush} SF/hr (Brush) × ${fmtDec2(r.accessMult)}`} result={`${fmtHrs(r.cutHrs)} cut-in hrs`} />
              <Formula expr={`${fmtQty(r.sfQty)} ÷ 100 × (${r.conditionPrep} ${line.condition} + ${r.sspcAdd} ${line.sspcPrep})`} result={`${fmtHrs(r.prepHrs)} prep hrs`} />
              {r.finishHrs > 0 && <Row label={`Finish level ${line.finishLevel}`} value={`${fmtHrs(r.finishHrs)} hrs`} hint="billed at finishing rate" />}
              <Formula expr={`${fmtQty(r.sfQty)} ÷ 100 × ${r.maskRate} (${line.tool})`} result={`${fmtHrs(r.maskHrs)} mask hrs`} />
              <Formula expr={`${fmtHrs(r.rawHrs)} hrs × difficulty ${fmtDec2(r.difficulty)}`} result={`${fmtHrs(r.totalHrs)} total hrs`} />
            </Section>

            <Section n={6} title="Dollars">
              <Formula
                expr={`${fmtHrs(r.totalHrs - r.finishHrs * r.difficulty)} hrs × ${fmtCurrency(rate)}${r.finishHrs > 0 ? ` + ${fmtHrs(r.finishHrs * r.difficulty)} hrs × ${fmtCurrency(config.globalAssumptions.laborRateFinishing)}` : ""}`}
                result={`${fmtCurrency(r.laborCost)} labor`}
              />
              <Formula
                expr={`${fmtQty(r.finishGal)} gal × ${fmtCurrency(r.finishProduct?.pricePerGal)}${r.primerProduct ? ` + ${fmtQty(r.primerGal)} gal × ${fmtCurrency(r.primerProduct.pricePerGal)}` : ""}`}
                result={`${fmtCurrency(r.materialCost)} material`}
              />
              <div className="mt-1 flex items-center justify-between rounded-[4px] border border-border bg-primary_tint px-3 py-2">
                <span className="text-[12px] font-semibold text-primary">Line total</span>
                <span className="text-[14px] font-bold tabular-nums text-primary">{fmtCurrency(r.lineTotal)}</span>
              </div>
              <p className="text-[11px] text-text_muted">
                Line totals are direct cost. Sundries, equipment, general conditions, overhead and profit are added on the Bid Form.
              </p>
            </Section>
          </>
        )}
        <div className="px-4 py-3">
          <Pill tone="neutral">Mock engine · simplified formulas</Pill>
        </div>
      </div>
    </aside>
  );
}
